import { useCallback, useEffect, useRef, useState } from 'react';

import urls from '../../../../../../db/polls/urls';
import { AttachmentRequestConfig, FileAttachment } from '../../../../../../db/types';
import { usePollContext } from '../../../../Context';
import { POLL_QUESTION_MAX_FONT_SIZE, POLL_QUESTION_MIN_FONT_SIZE } from '../../../constants';

const usePublishedQuestionDescription = () => {
  const { poll } = usePollContext();

  const getDownloadAttachmentRequestConfig = (
    attachment: FileAttachment
  ):
    | AttachmentRequestConfig<{
        pollId: MongoId;
        fileName: string;
      }>
    | undefined =>
    poll
      ? {
          url: urls.downloadPollFile,
          data: {
            pollId: poll.id,
            fileName: attachment.name,
          },
        }
      : undefined;

  const temporaryRichTextViewRef = useRef<HTMLDivElement | null>(null);
  const temporaryFontSize = useRef(POLL_QUESTION_MIN_FONT_SIZE);
  const [richTextFinalFontSize, setRichTextFinalFontSize] = useState<number | null>(null);

  const computeRichTextFontSize = useCallback(() => {
    if (!temporaryRichTextViewRef.current) return;

    temporaryRichTextViewRef.current.style.fontSize = `${temporaryFontSize.current}px`;

    const { scrollHeight } = temporaryRichTextViewRef.current;
    const clientHeight = temporaryRichTextViewRef.current.getBoundingClientRect().height;

    if (scrollHeight > Math.ceil(clientHeight)) {
      const finalFontSize = Math.max(temporaryFontSize.current - 1, POLL_QUESTION_MIN_FONT_SIZE);
      setRichTextFinalFontSize(finalFontSize);
      return;
    }

    if (temporaryFontSize.current === POLL_QUESTION_MAX_FONT_SIZE) {
      setRichTextFinalFontSize(temporaryFontSize.current);
      return;
    }

    temporaryFontSize.current += 1;

    // recursive call after incremented fontSize
    computeRichTextFontSize();
  }, []);

  useEffect(() => {
    computeRichTextFontSize();
  }, [computeRichTextFontSize]);

  return {
    getDownloadAttachmentRequestConfig,
    temporaryRichTextViewRef,
    richTextFinalFontSize,
  };
};

export default usePublishedQuestionDescription;
